/**
 * Where to put the group menu, in viewport coordinates.
 *
 * The sidebar is `overflow: hidden`, so a menu positioned inside it is clipped
 * to its box. Expanded, that cut the right-hand edge off a 260px menu in a
 * narrower rail. Collapsed, the menu was placed at `inset-inline-start: 100%`
 * — entirely outside that box — so it was clipped away completely: the button
 * worked, the menu rendered, and nothing appeared. The only way to change
 * group was to expand the sidebar first.
 *
 * So the menu leaves the sidebar and is placed against the viewport instead.
 * Measured rather than assumed, because the rail has two widths and the menu
 * has to clear whichever is in force.
 *
 * Pure, so the collapsed case can be tested without a browser.
 */

/** The bit of a DOMRect this needs. Keeps the tests free of a DOM. */
export interface Rect {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface Placement {
  top: number;
  left: number;
  width: number;
}

/** Gap between the trigger and the menu, and from the viewport edge. */
const GAP = 8;
const MARGIN = 8;

/** Wide enough for a group name and its size on one line. */
const MIN_WIDTH = 260;
const MAX_WIDTH = 320;

/**
 * @param beside Put the menu next to the trigger rather than under it.
 *
 * Passed in, never inferred from the trigger width. Inferring it meant
 * comparing the rail against the menu minimum, and an expanded 256px rail
 * against a 260px minimum came out as collapsed — a four-pixel difference
 * silently flipping the whole layout. The component knows which state the
 * sidebar is in; it should say so.
 */
export function placeMenu(
  trigger: Rect,
  viewport: Viewport,
  beside: boolean,
): Placement {
  const width = Math.min(
    MAX_WIDTH,
    Math.max(MIN_WIDTH, trigger.width),
    /* A narrow phone gets a narrow menu rather than one hanging off the edge. */
    viewport.width - MARGIN * 2,
  );

  /* Beside the rail when collapsed — the case that used to be invisible.
     Under the trigger otherwise, where a dropdown belongs. */
  const wantedLeft = beside ? trigger.right + GAP : trigger.left;
  const wantedTop = beside ? trigger.top : trigger.bottom + GAP;

  return {
    /* Clamped so it is on screen whichever side it wanted to be on. */
    left: clamp(
      wantedLeft,
      MARGIN,
      Math.max(MARGIN, viewport.width - width - MARGIN),
    ),
    top: clamp(wantedTop, MARGIN, Math.max(MARGIN, viewport.height - MARGIN)),
    width,
  };
}

function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(value, high));
}
